import React from 'react';

import { Box, Button, Typography } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';

import {
  EmployeeWorkLetterType,
  useGenerateWorkLetterForAdminMutation,
  useGenerateWorkLetterMutation,
  useGetEmployersListForWorkLetterForAdminQuery,
  useGetEmployersListForWorkLetterQuery,
} from 'src/api';
import { SelectField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import {
  buildEmployersListForVerificationRequest,
  convertEmployerListItemToEmployerDataItem,
} from 'src/utils/employer';
import { downloadFileByDownloadUrl } from 'src/utils/file';

import { FormFields, workLetterTypeSelectOptions } from './constants';
import { FormValues } from './types';
import { formValidationSchema } from './validation';

type GetWorkLetterFormProps = {
  onCloseModal: () => void;
  switchClosability: (closability: boolean) => void;
  employeeSsnEncrypted?: string;
  onGenerated?: () => void;
};

export const GetWorkLetterForm: React.FC<GetWorkLetterFormProps> = ({
  onCloseModal,
  switchClosability,
  employeeSsnEncrypted,
  onGenerated,
}) => {
  const { createApolloContext } = useNotification();
  const isAdminFlow = Boolean(employeeSsnEncrypted);

  const { data: employeeEmployersData } = useGetEmployersListForWorkLetterQuery({
    skip: isAdminFlow,
  });
  const { data: adminEmployersData } = useGetEmployersListForWorkLetterForAdminQuery({
    variables: { employeeSsnEncrypted: employeeSsnEncrypted ?? '' },
    skip: !isAdminFlow,
  });

  const employers = isAdminFlow
    ? adminEmployersData?.getEmployersListForWorkLetterForAdmin.employers
    : employeeEmployersData?.getEmployersListForWorkLetter.employers;

  const employersSelectOptions = React.useMemo(
    () => buildEmployersListForVerificationRequest(employers),
    [employers],
  );

  const [generateWorkLetterMutation] = useGenerateWorkLetterMutation({
    context: createApolloContext(),
  });
  const [generateWorkLetterForAdminMutation] = useGenerateWorkLetterForAdminMutation({
    context: createApolloContext(),
  });

  const handleSubmit = React.useCallback(
    async (formData: FormValues) => {
      if (employersSelectOptions.length === 0) return;

      switchClosability(false);

      try {
        const company = convertEmployerListItemToEmployerDataItem(formData.company);

        if (isAdminFlow && employeeSsnEncrypted) {
          const { data } = await generateWorkLetterForAdminMutation({
            variables: {
              employeeSsnEncrypted,
              company,
              letterType: formData.letterType,
            },
          });

          if (data?.generateWorkLetterPdfForAdmin) {
            downloadFileByDownloadUrl(
              data.generateWorkLetterPdfForAdmin.downloadUrl,
              data.generateWorkLetterPdfForAdmin.filename,
            );
          }
        } else {
          const { data } = await generateWorkLetterMutation({
            variables: {
              company,
              letterType: formData.letterType,
            },
          });

          if (data?.generateWorkLetterPdf) {
            downloadFileByDownloadUrl(
              data.generateWorkLetterPdf.downloadUrl,
              data.generateWorkLetterPdf.filename,
            );
          }
        }

        onGenerated?.();
        switchClosability(true);
        onCloseModal();
      } catch (error) {
        console.error(error);
        switchClosability(true);
      }
    },
    [
      employersSelectOptions,
      generateWorkLetterMutation,
      generateWorkLetterForAdminMutation,
      switchClosability,
      onCloseModal,
      onGenerated,
      isAdminFlow,
      employeeSsnEncrypted,
    ],
  );

  const initialValues: Partial<FormValues> = employersSelectOptions[0]
    ? {
        company: employersSelectOptions[0].value,
        letterType: EmployeeWorkLetterType.Employment,
      }
    : {};

  return (
    <React.Fragment>
      <Typography variant="body2" color="text.secondary">
        {isAdminFlow
          ? 'Select the employer from the list of employers associated with this employee, then choose the type of letter needed (Employment Letter or Immigration Letter) to generate a PDF document.'
          : 'Select your employer from the list of employers associated with your account, then choose the type of letter you need (Employment Letter or Immigration Letter) to generate a PDF document.'}
      </Typography>

      <Form<FormValues>
        key={employersSelectOptions[0]?.value.id ?? 'empty'}
        onSubmit={handleSubmit}
        validationSchema={formValidationSchema}
        initialValues={initialValues}
        subscription={{ valid: true, submitting: true }}
        containerProps={{ mt: 2 }}
      >
        {({ submitting, valid }) => (
          <React.Fragment>
            <Box>
              <Field
                name={FormFields.company}
                fullWidth
                label="Employer"
                component={SelectField}
                options={employersSelectOptions}
                required
              />
            </Box>

            <Box mt={2}>
              <Field
                name={FormFields.letterType}
                fullWidth
                label="Work Letter Type"
                component={SelectField}
                options={workLetterTypeSelectOptions}
                required
              />
            </Box>

            <Box display="flex" gap={3} mt={2} justifyContent="center" alignItems="center">
              <Button
                color="infoButton"
                variant="outlined"
                onClick={onCloseModal}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={submitting || !valid}
                loading={submitting}
              >
                Submit
              </Button>
            </Box>
          </React.Fragment>
        )}
      </Form>
    </React.Fragment>
  );
};
